import { Button } from "@/components/ui/button";
import { Command, CommandGroup, CommandItem, CommandList } from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useEffect, useState } from "react";
import { LuChevronRight, LuLoaderCircle, LuThumbsDown, LuThumbsUp } from "react-icons/lu";
import ReactTextareaAutosize from "react-textarea-autosize";
import { submitFeedback } from "../apiServices";

type Props = {
  chatConversationId: string;
  currentFeedback: { liked: boolean | null; description: string };
  updateFeedback: (
    feedback: {
      liked: boolean | null;
      description: string;
    },
    id: string,
  ) => void;
};

const NEGATIVE_FEEDBACK_DESCRIPTION_LIST = [
  {
    label: "Not factually correct",
    value: "not factually correct",
  },
  {
    label: "Couldn't answer due to lack of data",
    value: "couldn't answer due to lack of data",
  },
  {
    label: "Didn't fully follow instructions",
    value: "didn't fully follow instructions",
  },
  {
    label: "Made conclusions without proof",
    value: "made conclusions without proof",
  },
  {
    label: "Other",
    value: "other",
  },
];

export default function InsightChatFeedback({
  chatConversationId,
  currentFeedback,
  updateFeedback,
}: Props) {
  const [liked, setLiked] = useState<boolean | null>(currentFeedback?.liked);
  const [descriptionInput, setDescriptionInput] = useState<string>(
    currentFeedback?.description,
  );
  const [showTextarea, setShowTextarea] = useState<boolean>(false);
  const [openLikedPopup, setOpenLikedPopup] = useState<boolean>(false);
  const [openDislikedPopup, setOpenDislikedPopup] = useState<boolean>(false);
  const [feedbackLoading, setFeedbackLoading] = useState<boolean>(false);

  const handleDislikeCancelClick = (type: "back" | "cancel") => {
    if (type === "back") {
      setShowTextarea(false);
    } else {
      setOpenDislikedPopup(false);
    }
    // if (liked !== null && !liked) {
    //   setShowTextarea(false);
    // } else {
    //   setShowTextarea(false);
    // }
  };

  const sendFeedback = async (val: string = descriptionInput, like: boolean) => {
    setFeedbackLoading(true);
    try {
      const res =
        like !== null &&
        (await submitFeedback({
          chatConversationId,
          data: {
            liked: like,
            description: val,
          },
        }));
      updateFeedback({ liked: like, description: val }, chatConversationId);
      setLiked(like);
      setOpenLikedPopup(false);
      setOpenDislikedPopup(false);
      setFeedbackLoading(false);
    } catch (error) {
      console.error(error);
    } finally {
      setFeedbackLoading(false);
      setOpenLikedPopup(false);
      setOpenDislikedPopup(false);
    }
  };

  const handleOpenPopup = (val: boolean, type: "like" | "dislike") => {
    setLiked(null);
    setDescriptionInput("");

    setTimeout(() => {
      if (type === "like") {
        setLiked(currentFeedback.liked);
        setDescriptionInput(currentFeedback.liked ? currentFeedback.description : "");
        setOpenLikedPopup(val);
      } else {
        setOpenDislikedPopup(val);
        setLiked(currentFeedback.liked);
        if (currentFeedback.liked === false && currentFeedback.description) {
          const desc = NEGATIVE_FEEDBACK_DESCRIPTION_LIST.find((item) =>
            item.value.includes(currentFeedback.description),
          );
          if (typeof desc === "undefined") {
            setShowTextarea(true);
            setDescriptionInput(currentFeedback.description);
          }
        }
      }
    }, 100);
  };

  useEffect(() => {}, [chatConversationId, currentFeedback]);

  return (
    <>
      <div className="relative flex items-center gap-2 border rounded-full px-3 py-1">
        <p className="text-xs text-brand-primary-new font-medium">Was this helpful?</p>
        <Popover
          open={openLikedPopup}
          onOpenChange={(open) => handleOpenPopup(open, "like")}
          modal
        >
          <PopoverTrigger asChild>
            <span>
              <LuThumbsUp
                className={`text-title-secondary cursor-pointer hover:stroke-brand-primary ${
                  openLikedPopup ? "stroke-brand-primary" : ""
                } ${
                  currentFeedback.liked !== null
                    ? currentFeedback.liked
                      ? "stroke-brand-primary"
                      : "opacity-30"
                    : ""
                }`}
                size={15}
              />
            </span>
          </PopoverTrigger>
          <PopoverContent
            className="p-2"
            collisionPadding={10}
            side="top"
            align="end"
            sideOffset={10}
          >
            <ReactTextareaAutosize
              minRows={4}
              maxRows={8}
              disabled={feedbackLoading}
              value={descriptionInput}
              onChange={(e) => setDescriptionInput(e.target.value)}
              className="resize-none w-full p-2 border text-title-primary mt-1 focus:ring-0 focus:outline-none"
              placeholder="Write your feedback here..."
            ></ReactTextareaAutosize>
            <div className=" flex justify-end gap-1">
              <Button variant="outline" onClick={() => setOpenLikedPopup(false)}>
                Cancel
              </Button>
              <Button
                variant="brand"
                onClick={() => sendFeedback(undefined, true)}
                disabled={feedbackLoading}
              >
                {feedbackLoading && (
                  <LuLoaderCircle className="mr-2 h-4 w-4 animate-spin" />
                )}
                Submit
              </Button>
            </div>
          </PopoverContent>
        </Popover>
        <Popover
          open={openDislikedPopup}
          onOpenChange={(open) => handleOpenPopup(open, "dislike")}
          modal
        >
          <PopoverTrigger>
            <span>
              <LuThumbsDown
                className={`text-title-secondary cursor-pointer hover:stroke-brand-primary ${
                  openDislikedPopup ? "stroke-brand-primary" : ""
                } ${
                  currentFeedback.liked !== null
                    ? !currentFeedback.liked
                      ? "stroke-red"
                      : "opacity-30"
                    : ""
                }`}
                size={15}
              />
            </span>
          </PopoverTrigger>
          <PopoverContent
            className="p-0"
            collisionPadding={10}
            side="top"
            align="end"
            sideOffset={10}
          >
            <Command>
              <CommandList>
                <CommandGroup>
                  {!showTextarea ? (
                    NEGATIVE_FEEDBACK_DESCRIPTION_LIST?.map((item) => (
                      <CommandItem
                        key={item.value}
                        onSelect={(value) => {
                          if (value === "other") {
                            setShowTextarea(true);
                            setDescriptionInput("");
                          } else {
                            setDescriptionInput(value);
                            sendFeedback(value, false);
                          }
                        }}
                        disabled={feedbackLoading && descriptionInput === item.value}
                        className={`flex items-center justify-between cursor-pointer ${
                          item.value === currentFeedback.description
                            ? "!bg-brand-primary !text-white"
                            : ""
                        }`}
                      >
                        {item.label}
                        {item.value === "other" && <LuChevronRight />}
                        {feedbackLoading && descriptionInput === item.value && (
                          <LuLoaderCircle className="mr-2 h-4 w-4 animate-spin" />
                        )}
                      </CommandItem>
                    ))
                  ) : (
                    <ReactTextareaAutosize
                      minRows={4}
                      maxRows={8}
                      disabled={feedbackLoading}
                      value={descriptionInput}
                      onChange={(e) => setDescriptionInput(e.target.value)}
                      className="resize-none w-full p-2 border text-title-primary mt-1 outline-none ring-0 focus:outline-none focus:ring-0"
                      placeholder="Write your feedback here..."
                    ></ReactTextareaAutosize>
                  )}
                </CommandGroup>
              </CommandList>
            </Command>
            {showTextarea && (
              <div className="p-2 flex justify-end gap-1">
                <Button
                  variant="outline"
                  onClick={() =>
                    handleDislikeCancelClick(showTextarea ? "back" : "cancel")
                  }
                >
                  {showTextarea ? "Go Back" : "Cancel"}
                </Button>
                <Button
                  variant="brand"
                  onClick={() => sendFeedback(undefined, false)}
                  disabled={feedbackLoading}
                >
                  {feedbackLoading && (
                    <LuLoaderCircle className="mr-2 h-4 w-4 animate-spin" />
                  )}
                  Submit
                </Button>
              </div>
            )}
          </PopoverContent>
        </Popover>
      </div>
    </>
  );
}
